import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useProject } from "../../utils/hooks/useProject";
import API from "../../utils/API";
import Loading from "../../utils/components/Loading";
import DefaultReportOverview from "./DefaultReportOverview";
import DefaultReportTemplateSection from "./DefaultReportTemplateSection";

const OVERVIEW_TAB = "overview";
const MAX_TAB_LABEL = 28;
const VISIBLE_TABS = 5;

const truncate = (str) =>
  str && str.length > MAX_TAB_LABEL ? str.slice(0, MAX_TAB_LABEL) + "…" : str;

const OverflowDropdown = ({ overflowTemplates, allTemplates, activeTab, overflowHasActive, onTabChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div
      ref={ref}
      style={{ position: "relative" }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        className={`btn btn-sm px-3 ${overflowHasActive ? "btn-dark" : "btn-outline-dark"}`}
        style={{ fontSize: 13 }}
      >
        {overflowHasActive
          ? truncate(allTemplates.find((t) => t.id === activeTab)?.name || "")
          : `+${overflowTemplates.length}`}
        {" ▾"}
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            right: 0,
            zIndex: 1000,
            background: "#fff",
            border: "1px solid #dee2e6",
            borderRadius: 6,
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            minWidth: 220,
            paddingTop: 4,
            paddingBottom: 4,
          }}
        >
          {overflowTemplates.map((tmpl) => (
            <button
              key={tmpl.id}
              onClick={() => { onTabChange(tmpl.id); setOpen(false); }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "7px 16px",
                fontSize: 13,
                border: "none",
                background: activeTab === tmpl.id ? "#212529" : "transparent",
                color: activeTab === tmpl.id ? "#fff" : "#212529",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                if (activeTab !== tmpl.id) e.currentTarget.style.background = "#f8f9fa";
              }}
              onMouseLeave={(e) => {
                if (activeTab !== tmpl.id) e.currentTarget.style.background = "transparent";
              }}
              title={tmpl.name}
            >
              {tmpl.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const buildUrl = (projectId, templateId, page, size, creatorName, chartFilter) => {
  let url = `/project_default_report/${projectId}?template_id=${templateId}&page=${page}&size=${size}`;
  if (creatorName) url += `&creator_name=${encodeURIComponent(creatorName)}`;
  if (chartFilter?.sub_id_number) url += `&sub_id_number=${encodeURIComponent(chartFilter.sub_id_number)}`;
  if (chartFilter?.status_name) url += `&status_name_filter=${encodeURIComponent(chartFilter.status_name)}`;
  if (chartFilter?.field_code && chartFilter?.field_value) {
    url += `&field_code_filter=${encodeURIComponent(chartFilter.field_code)}&field_value_filter=${encodeURIComponent(chartFilter.field_value)}`;
  }
  return url;
};

const chartFilterFromParams = (params) => {
  const subId = params.get("sub_id_number");
  const statusName = params.get("status_name_filter");
  const fieldCode = params.get("field_code_filter");
  const fieldValue = params.get("field_value_filter");
  if (subId) return { type: "discipline", sub_id_number: subId, status_name: statusName || undefined };
  if (fieldCode && fieldValue) return { type: "field", field_code: fieldCode, field_value: fieldValue };
  return null;
};

const DefaultReport = ({ translate }) => {
  const { project } = useProject();
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(OVERVIEW_TAB);
  const [templatePages, setTemplatePages] = useState({});
  const [templateCreatorFilters, setTemplateCreatorFilters] = useState({});
  const [templateChartFilters, setTemplateChartFilters] = useState({});
  const loadedTabs = useRef(new Set());

  const syncSearchParams = useCallback((templateId, page, creatorName, chartFilter) => {
    if (!templateId || templateId === OVERVIEW_TAB) { setSearchParams({}); return; }
    const params = { template_id: templateId };
    if (page && page > 1) params.page = String(page);
    if (creatorName) params.creator_name = creatorName;
    if (chartFilter?.sub_id_number) params.sub_id_number = chartFilter.sub_id_number;
    if (chartFilter?.status_name) params.status_name_filter = chartFilter.status_name;
    if (chartFilter?.field_code) params.field_code_filter = chartFilter.field_code;
    if (chartFilter?.field_value) params.field_value_filter = chartFilter.field_value;
    setSearchParams(params);
  }, [setSearchParams]);

  const fetchReport = useCallback(() => {
    if (!project?.id) return;
    setLoading(true);
    loadedTabs.current = new Set();
    API.getData(`/project_default_report/${project.id}?page=1&size=20`)
      .then(({ data: res }) => {
        const templates = res?.data?.templates ?? [];
        const urlTemplateId = searchParams.get("template_id");
        const urlCreatorName = searchParams.get("creator_name") || null;
        const urlChartFilter = chartFilterFromParams(searchParams);
        const urlPage = parseInt(searchParams.get("page") || "1");
        const matchedId = urlTemplateId
          ? templates.find((t) => t.id === urlTemplateId)?.id ?? OVERVIEW_TAB
          : OVERVIEW_TAB;
        setData(res?.data ?? null);
        setActiveTab(matchedId);
        setTemplatePages(matchedId !== OVERVIEW_TAB && urlPage > 1 ? { [matchedId]: urlPage } : {});
        setTemplateCreatorFilters(matchedId !== OVERVIEW_TAB && urlCreatorName ? { [matchedId]: urlCreatorName } : {});
        setTemplateChartFilters(matchedId !== OVERVIEW_TAB && urlChartFilter ? { [matchedId]: urlChartFilter } : {});
        if (matchedId !== OVERVIEW_TAB) {
          loadedTabs.current.add(matchedId);
          API.getData(buildUrl(project.id, matchedId, urlPage, 20, urlCreatorName, urlChartFilter))
            .then(({ data: r }) => {
              const updated = r?.data?.templates?.find((t) => t.id === matchedId);
              if (updated) {
                setData((prev) => ({
                  ...prev,
                  templates: prev.templates.map((t) =>
                    t.id === matchedId ? { ...t, ...updated } : t
                  ),
                }));
              }
            })
            .catch(() => { loadedTabs.current.delete(matchedId); });
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  const mergeTemplateData = (templateId, updated) => {
    setData((prev) => ({
      ...prev,
      templates: prev.templates.map((t) =>
        t.id === templateId ? { ...t, ...updated } : t
      ),
    }));
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    syncSearchParams(tabId, 1, null, null);
    if (tabId === OVERVIEW_TAB || loadedTabs.current.has(tabId)) return;

    loadedTabs.current.add(tabId);
    API.getData(`/project_default_report/${project.id}?template_id=${tabId}&page=1&size=20`)
      .then(({ data: res }) => {
        const updated = res?.data?.templates?.find((t) => t.id === tabId);
        if (updated) mergeTemplateData(tabId, updated);
      })
      .catch(() => {
        loadedTabs.current.delete(tabId);
      });
  };

  const handlePageChange = (templateId, page) => {
    const creatorName = templateCreatorFilters[templateId] || null;
    const chartFilter = templateChartFilters[templateId] || null;
    setTemplatePages((prev) => ({ ...prev, [templateId]: page }));
    syncSearchParams(templateId, page, creatorName, chartFilter);
    setLoading(true);
    API.getData(buildUrl(project.id, templateId, page, 20, creatorName, chartFilter))
      .then(({ data: res }) => {
        const updated = res?.data?.templates?.find((t) => t.id === templateId);
        if (updated) mergeTemplateData(templateId, updated);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleCreatorFilter = (templateId, creatorName) => {
    const current = templateCreatorFilters[templateId] || null;
    const next = current === creatorName ? null : creatorName;
    const chartFilter = templateChartFilters[templateId] || null;
    setTemplateCreatorFilters((prev) => ({ ...prev, [templateId]: next }));
    setTemplatePages((prev) => ({ ...prev, [templateId]: 1 }));
    syncSearchParams(templateId, 1, next, chartFilter);
    setLoading(true);
    API.getData(buildUrl(project.id, templateId, 1, 20, next, chartFilter))
      .then(({ data: res }) => {
        const updated = res?.data?.templates?.find((t) => t.id === templateId);
        if (updated) mergeTemplateData(templateId, updated);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleClearFilters = (templateId) => {
    setTemplateCreatorFilters((prev) => ({ ...prev, [templateId]: null }));
    setTemplateChartFilters((prev) => ({ ...prev, [templateId]: null }));
    setTemplatePages((prev) => ({ ...prev, [templateId]: 1 }));
    syncSearchParams(templateId, 1, null, null);
    setLoading(true);
    API.getData(buildUrl(project.id, templateId, 1, 20, null, null))
      .then(({ data: res }) => {
        const updated = res?.data?.templates?.find((t) => t.id === templateId);
        if (updated) mergeTemplateData(templateId, updated);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleChartFilter = (templateId, chartFilter) => {
    setTemplateChartFilters((prev) => ({ ...prev, [templateId]: chartFilter }));
    setTemplatePages((prev) => ({ ...prev, [templateId]: 1 }));
    syncSearchParams(templateId, 1, templateCreatorFilters[templateId] || null, chartFilter);
    setLoading(true);
    API.getData(buildUrl(project.id, templateId, 1, 20, templateCreatorFilters[templateId] || null, chartFilter))
      .then(({ data: res }) => {
        const updated = res?.data?.templates?.find((t) => t.id === templateId);
        if (updated) mergeTemplateData(templateId, updated);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  if (!data && loading) return <Loading />;
  if (!data) return null;

  const activeTemplate = data.templates?.find((t) => t.id === activeTab);
  const t = (key) => translate(`project_settings.reports.default_report.${key}`);

  return (
    <div className="DefaultReport d-flex flex-column" style={{ height: "100%" }}>
      {/* Header */}
      {(() => {
        const templates = data.templates || [];
        const visibleTemplates = templates.slice(0, VISIBLE_TABS);
        const overflowTemplates = templates.slice(VISIBLE_TABS);
        const overflowHasActive = overflowTemplates.some((t) => t.id === activeTab);
        return (
          <div
            className="d-flex align-items-center justify-content-between px-4 py-2 gap-3"
            style={{ borderBottom: "1px solid #dee2e6", background: "#fff", flexShrink: 0 }}
          >
            <div className="d-flex align-items-center gap-3" style={{ minWidth: 0, flexShrink: 0 }}>
              {data.project_logo && (
                <>
                  <img
                    src={data.project_logo}
                    alt="logo"
                    style={{ height: 48, width: "auto", objectFit: "contain", flexShrink: 0 }}
                  />
                  <div style={{ width: 1, height: 40, backgroundColor: "#dee2e6", flexShrink: 0 }} />
                </>
              )}
              <div>
                <div className="fw-bold text-primary" style={{ fontSize: 17, lineHeight: 1.2 }}>
                  {data.project_name}
                </div>
                <div className="text-dark" style={{ fontSize: 13 }}>
                  {t("title").replace("${name}", "").trim()}
                </div>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2 justify-content-end">
              <button
                className={`btn btn-sm px-3 ${activeTab === OVERVIEW_TAB ? "btn-dark" : "btn-outline-dark"}`}
                style={{ fontSize: 13, whiteSpace: "nowrap" }}
                onClick={() => handleTabChange(OVERVIEW_TAB)}
              >
                {t("overview")}
              </button>

              {visibleTemplates.map((tmpl) => (
                <button
                  key={tmpl.id}
                  className={`btn btn-sm px-3 ${activeTab === tmpl.id ? "btn-dark" : "btn-outline-dark"}`}
                  style={{ fontSize: 13, whiteSpace: "nowrap" }}
                  onClick={() => handleTabChange(tmpl.id)}
                  title={tmpl.name}
                >
                  {truncate(tmpl.name)}
                </button>
              ))}

              {overflowTemplates.length > 0 && (
                <OverflowDropdown
                  overflowTemplates={overflowTemplates}
                  allTemplates={templates}
                  activeTab={activeTab}
                  overflowHasActive={overflowHasActive}
                  onTabChange={handleTabChange}
                />
              )}
            </div>
          </div>
        );
      })()}

      {/* Content */}
      <div className="flex-grow-1 overflow-auto p-4" style={{ position: "relative" }}>
        {loading && (
          <div
            style={{
              position: "absolute", inset: 0,
              background: "rgba(255,255,255,0.6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 10,
            }}
          >
            <Loading />
          </div>
        )}

        {activeTab === OVERVIEW_TAB && (
          <DefaultReportOverview data={data} translate={translate} />
        )}

        {activeTemplate && (
          <DefaultReportTemplateSection
            key={activeTemplate.id}
            template={activeTemplate}
            translate={translate}
            page={templatePages[activeTemplate.id] || 1}
            selectedCreator={templateCreatorFilters[activeTemplate.id] || null}
            selectedChartFilter={templateChartFilters[activeTemplate.id] || null}
            onPageChange={(page) => handlePageChange(activeTemplate.id, page)}
            onCreatorFilter={(name) => handleCreatorFilter(activeTemplate.id, name)}
            onChartFilter={(filter) => handleChartFilter(activeTemplate.id, filter)}
            onClearFilters={() => handleClearFilters(activeTemplate.id)}
          />
        )}
      </div>
    </div>
  );
};

export default DefaultReport;
